import React from 'react';
import { Container, Row, Col, Card, Table } from 'react-bootstrap';

const P = ({ width = 'col-6', height = 14, className = '', style = {} }) => (
  <span
    className={`placeholder ${width} ${className}`}
    style={{ height, borderRadius: 4, ...style }}
  />
);

const SkeletonCard = ({ children, className = '' }) => (
  <Card className={`pp-card ${className}`}>
    <Card.Body>
      <div className="placeholder-glow">{children}</div>
    </Card.Body>
  </Card>
);

/* ─── DASHBOARD ─── */
export const DashboardSkeleton = () => (
  <Container className="py-4">
    <div className="placeholder-glow mb-4">
      <P width="col-3" height={28} className="d-block" />
    </div>
    <Row className="g-3 mb-4">
      {[1, 2, 3, 4].map(i => (
        <Col key={i} sm={6} lg={3}>
          <SkeletonCard>
            <P width="col-5" height={12} className="mb-2 d-block" />
            <P width="col-3" height={28} className="mb-1 d-block" />
            <P width="col-7" height={10} className="d-block" />
          </SkeletonCard>
        </Col>
      ))}
    </Row>
    <Row className="g-3">
      <Col lg={8}>
        <SkeletonCard>
          <P width="col-4" height={18} className="mb-3 d-block" />
          <P width="col-12" height={200} className="d-block" style={{ borderRadius: 8 }} />
        </SkeletonCard>
      </Col>
      <Col lg={4}>
        <SkeletonCard>
          <P width="col-5" height={18} className="mb-3 d-block" />
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="d-flex align-items-center gap-2 mb-3">
              <P width="" height={32} style={{ width: 32, minWidth: 32, borderRadius: '50%' }} />
              <div className="flex-grow-1">
                <P width="col-8" height={10} className="d-block mb-1" />
                <P width="col-5" height={8} className="d-block" />
              </div>
            </div>
          ))}
        </SkeletonCard>
      </Col>
    </Row>
  </Container>
);

/* ─── TABLE PAGE ─── */
export const TablePageSkeleton = ({ columns = 5, rows = 6 }) => (
  <Container className="py-4">
    <div className="placeholder-glow mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <P width="col-3" height={28} />
        <P width="" height={36} style={{ width: 120, borderRadius: 6 }} />
      </div>
      <div className="d-flex gap-2 mb-3">
        <P width="" height={36} style={{ width: 220, borderRadius: 6 }} />
        <P width="" height={36} style={{ width: 100, borderRadius: 6 }} />
      </div>
    </div>
    <Card className="pp-card">
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table hover className="pp-table mb-0">
            <thead>
              <tr>
                {Array.from({ length: columns }).map((_, i) => (
                  <th key={i}>
                    <div className="placeholder-glow">
                      <P width={i === 0 ? 'col-8' : 'col-6'} height={10} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rows }).map((_, r) => (
                <tr key={r}>
                  {Array.from({ length: columns }).map((_, c) => (
                    <td key={c}>
                      <div className="placeholder-glow">
                        <P
                          width={c === 0 ? 'col-9' : c === columns - 1 ? 'col-5' : 'col-7'}
                          height={12}
                        />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  </Container>
);

/* ─── FORM PAGE ─── */
export const FormPageSkeleton = ({ fields = 6 }) => (
  <Container className="py-4">
    <div className="placeholder-glow mb-4">
      <P width="col-3" height={28} className="d-block mb-1" />
      <P width="col-5" height={12} className="d-block" />
    </div>
    <Card className="pp-card">
      <Card.Body>
        <div className="placeholder-glow">
          {Array.from({ length: fields }).map((_, i) => (
            <div key={i} className="mb-3">
              <P width="col-2" height={12} className="d-block mb-2" />
              <P width="col-12" height={38} className="d-block" style={{ borderRadius: 6 }} />
            </div>
          ))}
          <P width="" height={40} className="d-block mt-4" style={{ width: 140, borderRadius: 6 }} />
        </div>
      </Card.Body>
    </Card>
  </Container>
);

/* ─── CARD GRID ─── */
export const CardGridSkeleton = ({ cards = 6, colSize = { sm: 6, lg: 4 } }) => (
  <Container className="py-4">
    <div className="placeholder-glow mb-4">
      <div className="d-flex justify-content-between align-items-center">
        <P width="col-3" height={28} />
        <P width="" height={36} style={{ width: 120, borderRadius: 6 }} />
      </div>
    </div>
    <Row className="g-3">
      {Array.from({ length: cards }).map((_, i) => (
        <Col key={i} {...colSize}>
          <SkeletonCard className="h-100">
            <P width="col-6" height={18} className="d-block mb-2" />
            <P width="col-9" height={12} className="d-block mb-2" />
            <P width="col-4" height={12} className="d-block mb-3" />
            <div className="d-flex gap-2">
              <P width="" height={28} style={{ width: 70, borderRadius: 4 }} />
              <P width="" height={28} style={{ width: 70, borderRadius: 4 }} />
            </div>
          </SkeletonCard>
        </Col>
      ))}
    </Row>
  </Container>
);

/* ─── DETAIL PAGE ─── */
export const DetailPageSkeleton = () => (
  <Container className="py-4">
    <div className="placeholder-glow mb-4">
      <div className="d-flex align-items-center gap-2 mb-3">
        <P width="" height={32} style={{ width: 32, borderRadius: 6 }} />
        <P width="col-4" height={28} />
      </div>
    </div>
    <Row className="g-3 mb-4">
      {[1, 2, 3, 4].map(i => (
        <Col key={i} sm={6} lg={3}>
          <SkeletonCard>
            <P width="col-5" height={12} className="d-block mb-2" />
            <P width="col-3" height={24} className="d-block" />
          </SkeletonCard>
        </Col>
      ))}
    </Row>
    <Card className="pp-card">
      <Card.Body>
        <div className="placeholder-glow">
          <P width="col-3" height={18} className="d-block mb-3" />
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="d-flex align-items-center gap-3 mb-3 pb-3 border-bottom">
              <P width="col-2" height={12} />
              <P width="col-4" height={12} />
              <P width="col-1" height={22} style={{ borderRadius: 12 }} />
              <P width="col-2" height={12} />
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  </Container>
);

/* ─── CHAT / INBOX ─── */
export const ChatSkeleton = () => (
  <Container fluid className="py-4">
    <Row className="g-0" style={{ height: 'calc(100vh - 180px)' }}>
      <Col md={4} lg={3}>
        <Card className="pp-card h-100" style={{ borderRadius: '8px 0 0 8px' }}>
          <Card.Body className="p-2">
            <div className="placeholder-glow mb-3">
              <P width="col-12" height={36} className="d-block" style={{ borderRadius: 6 }} />
            </div>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="d-flex align-items-center gap-2 p-2 mb-1">
                <div className="placeholder-glow">
                  <P width="" height={40} style={{ width: 40, minWidth: 40, borderRadius: '50%' }} />
                </div>
                <div className="flex-grow-1 placeholder-glow">
                  <P width="col-7" height={12} className="d-block mb-1" />
                  <P width="col-10" height={10} className="d-block" />
                </div>
              </div>
            ))}
          </Card.Body>
        </Card>
      </Col>
      <Col md={8} lg={9}>
        <Card className="pp-card h-100" style={{ borderRadius: '0 8px 8px 0' }}>
          <Card.Body className="d-flex flex-column">
            <div className="placeholder-glow border-bottom pb-3 mb-3">
              <div className="d-flex align-items-center gap-2">
                <P width="" height={40} style={{ width: 40, borderRadius: '50%' }} />
                <div>
                  <P width="" height={14} className="d-block mb-1" style={{ width: 120 }} />
                  <P width="" height={10} className="d-block" style={{ width: 60 }} />
                </div>
              </div>
            </div>
            <div className="flex-grow-1" />
            <div className="placeholder-glow">
              <P width="col-12" height={44} className="d-block" style={{ borderRadius: 6 }} />
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  </Container>
);

/* ─── ADMIN DASHBOARD ─── */
export const AdminDashboardSkeleton = () => (
  <Container fluid className="py-3">
    <div className="placeholder-glow mb-3">
      <P width="col-3" height={28} className="d-block mb-1" />
      <P width="col-5" height={12} className="d-block" />
    </div>
    {/* Quick links skeleton */}
    <div className="d-flex gap-2 mb-3 flex-wrap placeholder-glow">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <P key={i} width="" height={32} style={{ width: 90, borderRadius: 10 }} />
      ))}
    </div>
    {/* Stat cards skeleton */}
    <Row className="g-2 mb-4">
      {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
        <Col key={i} xs={6} sm={6} md={4} lg={3}>
          <SkeletonCard>
            <P width="col-7" height={10} className="d-block mb-2" />
            <P width="col-4" height={22} className="d-block" />
          </SkeletonCard>
        </Col>
      ))}
    </Row>
    {/* Tables skeleton */}
    <Row className="g-3">
      {[1, 2].map(t => (
        <Col key={t} lg={6}>
          <Card className="pp-card">
            <div className="placeholder-glow" style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid #e7e5e4' }}>
              <P width="col-4" height={14} className="d-block mb-1" />
              <P width="col-6" height={10} className="d-block" />
            </div>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="pp-table mb-0">
                  <thead>
                    <tr>
                      {[1, 2, 3, 4].map(c => (
                        <th key={c}><div className="placeholder-glow"><P width="col-7" height={10} /></div></th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3, 4, 5].map(r => (
                      <tr key={r}>
                        {[1, 2, 3, 4].map(c => (
                          <td key={c}><div className="placeholder-glow"><P width={c === 1 ? 'col-9' : 'col-6'} height={12} /></div></td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  </Container>
);

/* ─── SETUP (QR) ─── */
export const SetupSkeleton = () => (
  <Container className="py-4">
    <div className="placeholder-glow mb-4 text-center">
      <P width="col-4" height={28} className="d-block mx-auto mb-2" />
      <P width="col-6" height={12} className="d-block mx-auto" />
    </div>
    <Row className="justify-content-center">
      <Col md={8} lg={6}>
        <Card className="pp-card text-center">
          <Card.Body className="py-5">
            <div className="placeholder-glow">
              <P width="" height={240} className="d-block mx-auto mb-4" style={{ width: 240, borderRadius: 12 }} />
              <P width="col-6" height={14} className="d-block mx-auto mb-2" />
              <P width="col-4" height={12} className="d-block mx-auto" />
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  </Container>
);

/* ─── REPORT ─── */
export const ReportSkeleton = () => (
  <Container className="py-4">
    <div className="placeholder-glow mb-4">
      <div className="d-flex justify-content-between align-items-center">
        <P width="col-3" height={28} />
        <div className="d-flex gap-2">
          <P width="" height={36} style={{ width: 120, borderRadius: 6 }} />
          <P width="" height={36} style={{ width: 100, borderRadius: 6 }} />
        </div>
      </div>
    </div>
    <Row className="g-3 mb-4">
      {[1, 2, 3, 4].map(i => (
        <Col key={i} sm={6} lg={3}>
          <SkeletonCard>
            <P width="col-5" height={12} className="d-block mb-2" />
            <P width="col-3" height={24} className="d-block" />
          </SkeletonCard>
        </Col>
      ))}
    </Row>
    <Card className="pp-card mb-4">
      <Card.Body>
        <div className="placeholder-glow">
          <P width="col-3" height={18} className="d-block mb-3" />
          <P width="col-12" height={200} className="d-block" style={{ borderRadius: 8 }} />
        </div>
      </Card.Body>
    </Card>
    <Card className="pp-card">
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table hover className="pp-table mb-0">
            <thead>
              <tr>
                {[1, 2, 3, 4, 5].map(i => (
                  <th key={i}><div className="placeholder-glow"><P width="col-6" height={10} /></div></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4].map(r => (
                <tr key={r}>
                  {[1, 2, 3, 4, 5].map(c => (
                    <td key={c}><div className="placeholder-glow"><P width="col-7" height={12} /></div></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  </Container>
);

/* ─── INLINE TABLE (for use inside existing card with p-0) ─── */
export const InlineTableSkeleton = ({ rows = 5, cols = 5 }) => (
  <div className="table-responsive">
    <Table hover className="pp-table mb-0">
      <thead>
        <tr>
          {Array.from({ length: cols }).map((_, c) => (
            <th key={c}>
              <div className="placeholder-glow">
                <P width={c === 0 ? 'col-8' : 'col-6'} height={10} />
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r}>
            {Array.from({ length: cols }).map((_, c) => (
              <td key={c}>
                <div className="placeholder-glow">
                  <P
                    width={c === 0 ? 'col-9' : c === cols - 1 ? 'col-5' : 'col-7'}
                    height={12}
                  />
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
);

/* ─── GENERIC ─── */
export const GenericSkeleton = () => (
  <Container className="py-4">
    <div className="placeholder-glow mb-4">
      <P width="col-4" height={28} className="d-block mb-2" />
      <P width="col-6" height={12} className="d-block" />
    </div>
    <Card className="pp-card">
      <Card.Body>
        <div className="placeholder-glow">
          <P width="col-12" height={16} className="d-block mb-3" />
          <P width="col-10" height={16} className="d-block mb-3" />
          <P width="col-8" height={16} className="d-block mb-3" />
          <P width="col-11" height={16} className="d-block mb-3" />
          <P width="col-6" height={16} className="d-block" />
        </div>
      </Card.Body>
    </Card>
  </Container>
);

/* ─── AUTH PAGE SKELETON (Login / Register / Forgot Password) ─── */
export const AuthSkeleton = () => (
  <div className="auth-layout">
    <div className="auth-left">
      <div className="auth-skeleton-dots">
        <span /><span /><span />
      </div>
    </div>

    <div className="auth-right">
      <div className="auth-form-container" style={{ animation: 'none' }}>
        <div className="placeholder-glow">
          <P width="col-5" height={26} className="d-block mb-2" />
          <P width="col-8" height={12} className="d-block mb-4" />
          {[1, 2].map(i => (
            <div key={i} className="mb-3">
              <P width="col-3" height={10} className="d-block mb-2" />
              <P width="col-12" height={42} className="d-block" style={{ borderRadius: 8 }} />
            </div>
          ))}
          <div className="d-flex justify-content-between mb-3">
            <P width="" height={10} style={{ width: 100 }} />
            <P width="" height={10} style={{ width: 110 }} />
          </div>
          <P width="col-12" height={44} className="d-block mb-3" style={{ borderRadius: 8 }} />
          <div className="text-center">
            <P width="col-7" height={10} className="d-block mx-auto" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ─── LANDING PAGE SKELETON ─── */
export const LandingPageSkeleton = () => (
  <div style={{ background: '#0a0a1a', color: '#fff', minHeight: '100vh' }}>
    {/* Nav skeleton */}
    <div style={{ padding: '16px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <Container>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2 placeholder-glow">
            <P width="" height={36} style={{ width: 36, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.08)' }} />
            <P width="" height={18} style={{ width: 120, backgroundColor: 'rgba(255,255,255,0.08)' }} />
          </div>
          <div className="d-none d-md-flex align-items-center gap-3 placeholder-glow">
            {[80, 60, 80, 40].map((w, i) => (
              <P key={i} width="" height={12} style={{ width: w, backgroundColor: 'rgba(255,255,255,0.06)' }} />
            ))}
            <P width="" height={34} style={{ width: 100, borderRadius: 20, backgroundColor: 'rgba(37,211,102,0.15)' }} />
          </div>
        </div>
      </Container>
    </div>
    {/* Hero skeleton */}
    <Container style={{ paddingTop: 80, paddingBottom: 60 }}>
      <Row className="align-items-center">
        <Col lg={7}>
          <div className="placeholder-glow">
            <P width="" height={26} className="d-block mb-3" style={{ width: 280, borderRadius: 20, backgroundColor: 'rgba(37,211,102,0.1)' }} />
            <P width="col-11" height={36} className="d-block mb-2" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />
            <P width="col-9" height={36} className="d-block mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />
            <P width="col-10" height={14} className="d-block mb-2" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
            <P width="col-8" height={14} className="d-block mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
            <div className="d-flex gap-2 flex-wrap mb-3">
              {[120, 150, 110, 140].map((w, i) => (
                <P key={i} width="" height={28} style={{ width: w, borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.06)' }} />
              ))}
            </div>
            <div className="d-flex gap-3 mb-4">
              <P width="" height={48} style={{ width: 170, borderRadius: 12, backgroundColor: 'rgba(37,211,102,0.15)' }} />
              <P width="" height={48} style={{ width: 170, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.06)' }} />
            </div>
            <div className="d-flex gap-3 flex-wrap">
              {[140, 130, 120, 110].map((w, i) => (
                <P key={i} width="" height={10} style={{ width: w, backgroundColor: 'rgba(255,255,255,0.04)' }} />
              ))}
            </div>
          </div>
        </Col>
        <Col lg={5} className="d-none d-lg-flex justify-content-center">
          <div className="placeholder-glow">
            <P width="" height={320} style={{ width: 320, borderRadius: '50%', backgroundColor: 'rgba(37,211,102,0.06)' }} />
          </div>
        </Col>
      </Row>
    </Container>
    {/* Stats bar skeleton */}
    <Container>
      <div className="d-flex flex-wrap justify-content-center gap-4 placeholder-glow" style={{ padding: '40px 0' }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{ textAlign: 'center', minWidth: 140 }}>
            <P width="" height={32} className="d-block mx-auto mb-2" style={{ width: 70, backgroundColor: 'rgba(37,211,102,0.1)' }} />
            <P width="" height={12} className="d-block mx-auto mb-1" style={{ width: 100, backgroundColor: 'rgba(255,255,255,0.08)' }} />
            <P width="" height={10} className="d-block mx-auto" style={{ width: 130, backgroundColor: 'rgba(255,255,255,0.04)' }} />
          </div>
        ))}
      </div>
    </Container>
  </div>
);
